import { useState, useRef } from 'react';
import styled from 'styled-components';
import { Stack, Dropdown, PopoverMenu } from '@koob/margaret';
import { useTranslation } from 'react-i18next';
import {
  KoobButton,
  KoobInputSelect,
  KoobInputSlider,
  KoobInputText,
} from '@koob/ui';

const MAX_DURATION = 50;

const FiltersPopoverMenu = styled(PopoverMenu)`
  overflow: visible !important;
  width: 420px;
  max-height: none;
  position: relative;
  left: -210px;
  /* the trigger now sits in normal page flow (not the elevated Header), so
     the popover must stack above the card grid on its own */
  z-index: 20;
`;

const FilterRow = styled(Stack).attrs({
  direction: 'column',
  gutterSize: 0.5,
  paddingLeft: 1,
  paddingRight: 1,
  paddingTop: 1,
})``;

const FilterExperiences = ({
  filters,
  updateFilterValue,
  onReset,
  languageOptions,
  themes,
  themesLoading,
  segments,
  segmentsLoading,
  sustainableLevels,
}) => {
  const { t } = useTranslation('experiences');
  const filtersDropdownRef = useRef();
  const [resetToken, setResetToken] = useState(0);

  const handleReset = () => {
    onReset();
    setResetToken((token) => token + 1);
  };

  return (
    <Dropdown
      ref={filtersDropdownRef}
      trigger={
        <KoobButton variant="secondary" asChild>
          <span>
            <Stack alignY="center" gutterSize={0.5}>
              <Stack>
                <i className="fa-light fa-bars-filter" />
              </Stack>
              <span>{t('filters.button')}</span>
            </Stack>
          </span>
        </KoobButton>
      }
    >
      <FiltersPopoverMenu>
        <FilterRow>
          <p>{t('search.startLocation')}</p>
          <KoobInputText
            key={`start-${resetToken}`}
            placeholder={t('search.startLocation')}
            defaultValue={filters.startingPoint}
            onKeyUp={(event) =>
              updateFilterValue('startingPoint', event.target.value)
            }
            style={{ width: '100%' }}
          />
        </FilterRow>

        <FilterRow>
          <p>{t('search.endLocation')}</p>
          <KoobInputText
            key={`end-${resetToken}`}
            placeholder={t('search.endLocation')}
            defaultValue={filters.endingPoint}
            onKeyUp={(event) =>
              updateFilterValue('endingPoint', event.target.value)
            }
            style={{ width: '100%' }}
          />
        </FilterRow>

        <FilterRow>
          <p>{t('search.expDuration')}</p>
          <KoobInputSlider
            value={[
              filters?.expDuration?.min ?? 0,
              filters?.expDuration?.max ?? MAX_DURATION,
            ]}
            onValueChange={([min, max]) =>
              updateFilterValue(
                'expDuration',
                min === 0 && max === MAX_DURATION ? null : { min, max },
              )
            }
            min={0}
            max={MAX_DURATION}
            formatValue={(value) =>
              t('search.expDurationCount', { count: value })
            }
          />
        </FilterRow>

        <FilterRow>
          <p>{t('search.languages')}</p>
          <KoobInputSelect
            placeholder={t('search.languages')}
            multiple
            options={languageOptions}
            value={(filters?.languages || []).map((item) => item.value)}
            onValueChange={(values) =>
              updateFilterValue(
                'languages',
                languageOptions.filter((option) =>
                  values.includes(option.value),
                ),
              )
            }
            clearable
          />
        </FilterRow>

        <FilterRow>
          <p>{t('search.experienceTheme')}</p>
          <KoobInputSelect
            placeholder={t('search.experienceTheme')}
            multiple
            loading={themesLoading}
            options={themes.map((item) => ({
              value: item.id,
              label: item.displayName,
            }))}
            value={(filters?.themes || []).map((item) => item.id)}
            onValueChange={(ids) =>
              updateFilterValue(
                'themes',
                themes.filter((item) => ids.includes(item.id)),
              )
            }
            clearable
          />
        </FilterRow>

        <FilterRow>
          <p>{t('search.experienceSegment')}</p>
          <KoobInputSelect
            placeholder={t('search.experienceSegment')}
            multiple
            loading={segmentsLoading}
            options={segments.map((item) => ({
              value: item.id,
              label: item.displayName,
            }))}
            value={(filters?.segments || []).map((item) => item.id)}
            onValueChange={(ids) =>
              updateFilterValue(
                'segments',
                segments.filter((item) => ids.includes(item.id)),
              )
            }
            clearable
          />
        </FilterRow>

        <FilterRow>
          <p>{t('search.sustainableLevel')}</p>
          <KoobInputSelect
            placeholder={t('search.sustainableLevel')}
            multiple
            searchable
            options={sustainableLevels}
            value={(filters?.sustainableLevels || []).map((item) => item.value)}
            onValueChange={(values) =>
              updateFilterValue(
                'sustainableLevels',
                sustainableLevels.filter((option) =>
                  values.includes(option.value),
                ),
              )
            }
            clearable
          />
        </FilterRow>

        <Stack
          alignX="flex-end"
          alignY="baseline"
          gutterSize={1}
          paddingTop={1}
          paddingRight={1}
          paddingBottom={1}
        >
          <KoobButton variant="secondary" type="button" onClick={handleReset}>
            {t('misc:reset')}
          </KoobButton>
          <KoobButton
            variant="primary"
            type="button"
            onClick={() => {
              if (Boolean(filtersDropdownRef.current)) {
                filtersDropdownRef.current.close();
              }
            }}
          >
            {t('misc:search')}
          </KoobButton>
        </Stack>
      </FiltersPopoverMenu>
    </Dropdown>
  );
};

export default FilterExperiences;
